import React from 'react'
import { TableHost, TableScrollingContainer } from '../../components/Table'
import {
  ImageCell,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TextCell,
} from '../../components/Table/ClickableTable'
import { TableSearchbar } from '../../payouts/components/TableSearchbar'
import { observer } from 'mobx-react'
import InfiniteScrollLoader from '../../components/InfiniteScrollLoader'
import { AccountMergeRequestItemViewModel } from './AccountMergeRequestItemViewModel'
import { match } from 'utils/pattern-match-util'
import {
  Dropdown,
  DropdownItem,
  DropdownTitle,
} from '../../components/Dropdown'
import Icon from '../../components/Icon'
import { AccountMergeRequestsViewModel } from './AccountMergeRequestsViewModel'
import { StatusOrActions } from './AccountMergeRequestSearchViewModel'
import { PillCell } from '../../jobs/views/JobManagement/JobsTable/JobsTableRow'
import { formatDate } from 'date-fns'

interface MergeRequestsTableProps {
  vm: AccountMergeRequestsViewModel
}

export const MergeRequestsTable = observer(function MergeRequestsTable({
  vm,
}: MergeRequestsTableProps) {
  return (
    <TableHost>
      <TableSearchbar
        searchText={vm.searchViewModel.searchText}
        onSearchTextChange={(t) => vm.searchViewModel.setSearchText(t)}
      />
      <TableScrollingContainer>
        <Table striped>
          <TableHead>
            <TableHeader alignment="left">Name</TableHeader>
            <TableHeader alignment="left">Email</TableHeader>
            <TableHeader alignment="left">Primary ID</TableHeader>
            <TableHeader alignment="left">Duplicate ID</TableHeader>
            <TableHeader alignment="left">Date requested</TableHeader>
            <StatusOrActionsHeader
              statusOrActions={vm.searchViewModel.statusOrActions}
            />
          </TableHead>
          <TableBody>
            {vm.searchViewModel.mergeRequests.map((itemVM) => (
              <MergeRequestRow
                key={itemVM.id}
                itemVM={itemVM}
                onMergeClick={() => vm.mergeAccountsDialogVM.show(itemVM)}
                onRejectClick={() => vm.rejectMergeDialogVM.show(itemVM)}
                onIgnoreClick={() => vm.ignoreMergeRequestVM.show(itemVM)}
              />
            ))}
          </TableBody>
        </Table>
        {!vm.searchViewModel.scrollProvider.isDone && (
          <InfiniteScrollLoader
            scrollProvider={vm.searchViewModel.scrollProvider}
          />
        )}
      </TableScrollingContainer>
    </TableHost>
  )
})

/**
 * Props for the table's status or actions menu column.
 */
interface StatusOrActionsHeaderProps {
  statusOrActions: StatusOrActions
}

/**
 * The table's status or (invisible) actions menu column.
 */
const StatusOrActionsHeader = observer(function StatusOrActionsHeader({
  statusOrActions,
}: StatusOrActionsHeaderProps) {
  return match(statusOrActions, {
    [StatusOrActions.Status]: () => (
      <TableHeader alignment="left">Status</TableHeader>
    ),
    [StatusOrActions.Actions]: () => <TableHeader alignment="right" />,
  })
})

/**
 * Props for the Merge Request Row.
 */
interface MergeRequestRowProps {
  itemVM: AccountMergeRequestItemViewModel
  onMergeClick: () => any
  onRejectClick: () => any
  onIgnoreClick: () => any
}

/**
 * An individual merge request row/record.
 */
const MergeRequestRow = observer(function MergeRequestRow({
  itemVM,
  onMergeClick,
  onRejectClick,
  onIgnoreClick,
}: MergeRequestRowProps) {
  return (
    <TableRow href={itemVM.hubspotLink} target="_blank" alwaysShowPointer>
      <ImageCell
        title={itemVM.displayName}
        image={itemVM.avatar}
        imageFallback={itemVM.avatarFallback}
      />
      <TableCell>{itemVM.email}</TableCell>
      <TableCell>{itemVM.primaryAccountId}</TableCell>
      <TableCell>{itemVM.duplicateAccountId}</TableCell>
      <TableCell>{formatDate(itemVM.dateCreated, 'PP')}</TableCell>
      <StatusOrActionsCell
        itemViewModel={itemVM}
        onMergeClick={onMergeClick}
        onRejectClick={onRejectClick}
        onIgnoreClick={onIgnoreClick}
      />
    </TableRow>
  )
})

/**
 * Props for the Status Or Actions Cell.
 */
interface StatusOrActionsProps {
  itemViewModel: AccountMergeRequestItemViewModel
  onMergeClick: () => any
  onRejectClick: () => any
  onIgnoreClick: () => any
}

/**
 * A cell containing either the merge request's status or its actions menu.
 */
const StatusOrActionsCell = observer(function StatusOrActionsProps({
  itemViewModel,
  onMergeClick,
  onRejectClick,
  onIgnoreClick,
}: StatusOrActionsProps) {
  return match(itemViewModel.statusOrActions, {
    [StatusOrActions.Status]: () => (
      <PillCell>{itemViewModel.displayStatus}</PillCell>
    ),
    [StatusOrActions.Actions]: () => (
      <TextCell alignment="right">
        <MergeRequestActions
          onMergeClick={onMergeClick}
          onRejectClick={onRejectClick}
          onIgnoreClick={onIgnoreClick}
        />
      </TextCell>
    ),
  })
})

/**
 * Props for a merge request's actions.
 */
interface MergeRequestActionsProps {
  onMergeClick: () => any
  onRejectClick: () => any
  onIgnoreClick: () => any
}

/**
 * A merge request's actions menu.
 */
const MergeRequestActions = observer(function MergeRequestActions({
  onMergeClick,
  onRejectClick,
  onIgnoreClick,
}: MergeRequestActionsProps) {
  return (
    <Dropdown
      alignContent="right"
      autoDismiss
      position="bottom-end"
      button={<Icon name="more" />}
    >
      <DropdownTitle>Actions</DropdownTitle>
      <DropdownItem onClick={onMergeClick}>Merge accounts</DropdownItem>
      <DropdownItem onClick={onRejectClick}>Reject merge request</DropdownItem>
      <DropdownItem onClick={onIgnoreClick}>Ignore merge request</DropdownItem>
    </Dropdown>
  )
})
